<template>
  <div class="w-full max-h-full h-full px-6 py-4">
    <OTable
      v-if="_service"
      class="w-full"
      container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
      :data="_service.employees"
      :headers="[
        {text: $t('general.enabled')},
        {text: $t('general.employee')},
        {text: $t('general.price')},
        {text: $t('general.duration')},
        {text: $t('general.processing')},
        {text: $t('general.finishing')},
        {text: $t('general.online')},
        {text: $t('general.availableDays')}
      ]"
      :limit="20"
      :thead-class="{
        thead: 'border-b dark:border-gray-900',
        tr: 'border-blue-700',
        th: 'font-normal p-2 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-white text-base sticky top-0 text-left',
        content: 'flex item-center'
      }"
      :tbody-class="{
        tfoot: '',
        tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
        td: 'py-3 px-2 text-base',
        stripe: 'transition-all duration-200 bg-gray-50/50 dark:bg-white/[0.01]'
      }"
      :filter-keys="[
        'employee.displayName',
        'price'
      ]"
      striped
      filter
      collapse
      scroll
    >
      <template #row="props">
        <td :class="props.tdClass">
          <ToggleField v-model="props.row.enabled" @change="handleToggleEnable(props.row)" />
        </td>
        <td :class="props.tdClass" class="w-48 line-clamp-3">
          <span :class="{ 'text-gray-500 dark:text-gray-400' : !props.row.enabled }">{{ props.row.employee.displayName }}</span>
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <CurrencyField v-model="props.row.price" :disabled="!props.row.enabled" :placeholder="_service.price" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.price }" @change="handleEmployeePriceChange" />
        </td>
        <td :class="props.tdClass" class="min-w-32">
          <NumberField
            v-model="props.row.duration"
            slim
            :min="0"
            :disabled="!props.row.enabled"
            :placeholder="_service.duration"
            :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.duration }"
          />
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <NumberField v-model="props.row.processing" slim :disabled="!props.row.enabled" :placeholder="_service.processing" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.processing }" />
        </td>
        <td :class="props.tdClass" class="min-w-24">
          <NumberField v-model="props.row.finishing" slim :disabled="!props.row.enabled" :placeholder="_service.finishing" :class="{ 'ring-2 ring-yellow-300 ring-offset-1' : !!props.row.finishing }" />
        </td>
        <td :class="props.tdClass">
          <!--<div class="flex justify-center">
            <CheckboxField v-if="props.row.enabled" v-model="props.row.allowInternet" :disabled="!props.row.enabled" />
            <span v-else class="h-4 w-4 border rounded" />
          </div>-->

          <AllowInternet
            :value="props.row.allowInternet"
            :default="_service.allowInternet"
            :enabled="props.row.enabled"
            @change="(val) => props.row.allowInternet = val"
          />
        </td>
        <td :class="props.tdClass" class="w-32">
          <div clas="flex items-center">
            <p class="inline mr-2 text-sm text-gray-800 dark:text-gray-100" :class="{ 'font-semibold text-red-600' : props.row.enabled && props.row.allowedDays && props.row.isAllowedDaysLimited}">
              {{ props.row.enabled && props.row.allowedDays ? $t(props.row.availableDaysLabel) : $t('general.none') }}
            </p>
            <OButton v-if="props.row.enabled" flat size="xs" icon="edit" @click="props.toggle" />
          </div>
        </td>
      </template>

      <template #collapse="{ row }">
        <div>
          <div class="mb-3">
            <h4 class="text-sm text-gray-800 dark:text-gray-100">
              {{ $t('general.availableDays') }}
            </h4>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ $t('employee.limitService') }}
            </p>
          </div>

          <div class="flex items-center">
            <template v-if="row.allowedDays">
              <div
                v-for="(day, key) in row.allowedDays"
                :key="key"
                class="pb-2 pr-2"
              >
                <CheckboxField
                  v-slot="{ isChecked }"
                  v-model="row.allowedDays[key]"
                  :label="key"
                  base-class="hidden"
                  active-class=""
                >
                  <div
                    :class="[
                      'px-2 py-1 text-sm rounded capitalize flex items-center cursor-pointer transform hover:scale-104 transition-transform',
                      isChecked ? 'bg-green-400 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    ]"
                  >
                    <OIcon :icon="isChecked ? 'check' : 'cross'" fill class="mr-2" :size="12" />
                    <span>{{ key }}</span>
                  </div>
                </CheckboxField>
              </div>
            </template>
          </div>
        </div>
      </template>
    </OTable>
  </div>
</template>

<script setup lang="ts">
import AllowInternet from '@/components/employee/elements/AllowInternet.vue'
import { OTable, OButton } from '@/components/elements'
import {
  ToggleField,
  CurrencyField,
  NumberField,
  CheckboxField,
} from '@/components/fields'
import type { Service } from '~/_api/models/service'

interface Props {
  handleEmployeePriceChange: () => void
}

const { handleEmployeePriceChange } = defineProps<Props>()

// Model bindings
const _service = defineModel<Service>('service')

// Methods
const handleToggleEnable = (employeeService: any): void => {
  if (employeeService.enabled && !employeeService.allowedDays) {
    const allowedDays = { 
      monday: true, 
      tuesday: true, 
      wednesday: true, 
      thursday: true, 
      friday: true, 
      saturday: true, 
      sunday: true 
    }
    employeeService.allowedDays = allowedDays
  }
}
</script>
